/**
 * __STOP_FIX_ROUTE__ (2026-09-26) a driver fixed a stop's address from the Driver app (1.24).
 *
 * The hub (Billing-Manager POST /api/driver-app/stop-address) moves the stop -- address,
 * latitude, longitude -- and leaves a system load_comments row with
 * status_transition = 'stop address fixed'. The route engine lives here in the board, so the
 * driver's day (driver_daily_routes / routes / route_loads and the stamped stop ETAs) is
 * refreshed here: every open board listens for that comment and runs the same
 * refreshRouteAfterEdit an edit in EditLoadModal runs (detach + recompute; finished jobs are
 * left alone). load_comments is in the realtime publication (routes are not recomputed by the
 * database). Best effort: never throws; once per comment per tab.
 */
import { supabase } from '../supabaseClient';

export const STOP_FIX_MARKER = 'stop address fixed';

/** Is this new load_comments row a driver's address fix? */
export const isStopFixComment = (row) =>
  !!row && row.is_system === true && String(row.status_transition || '').toLowerCase() === STOP_FIX_MARKER && !!row.load_id;

/**
 * handler(row) -> Promise<'refreshed' | 'skipped' | 'seen' | 'no-driver' | 'error'>
 * deps: fetchLoad(loadId) -> {id, driver_id, status, scheduled_date} | null; refresh(args) -> Promise.
 */
export const makeStopFixHandler = ({ fetchLoad, refresh }) => {
  const seen = new Set();
  return async (row) => {
    if (!isStopFixComment(row)) return 'skipped';
    const key = row.id || `${row.load_id}|${row.created_at}`;
    if (seen.has(key)) return 'seen';
    seen.add(key);
    try {
      const load = await fetchLoad(row.load_id);
      if (!load || !load.driver_id) return 'no-driver';
      const d = load.scheduled_date || null;
      await refresh({ loadId: load.id, driverId: load.driver_id, status: load.status, oldDate: d, newDate: d });
      return 'refreshed';
    } catch (e) {
      console.warn('stop-fix route refresh failed:', e);
      return 'error';
    }
  };
};

const fetchLoadRow = async (loadId) => {
  const { data, error } = await supabase.from('loads').select('id, driver_id, status, scheduled_date').eq('id', loadId).single();
  if (error) throw error;
  return data;
};

const runRefresh = async (args) => {
  const { refreshRouteAfterEdit } = await import('./routingEngineService');
  await refreshRouteAfterEdit(args);
};

/** Dashboard mounts this once; returns the unsubscribe. */
export const subscribeStopFixRouteRefresh = () => {
  const handle = makeStopFixHandler({ fetchLoad: fetchLoadRow, refresh: runRefresh });
  const ch = supabase
    .channel('stop_fix_route_refresh')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'load_comments' }, (p) => { handle(p.new); })
    .subscribe();
  return () => supabase.removeChannel(ch);
};

export default { STOP_FIX_MARKER, isStopFixComment, makeStopFixHandler, subscribeStopFixRouteRefresh };
